/**
 * Created by Thomas Mark
 * Date: 2025-11-01
 * 
 * ImageGallery - Für jedes Bild eines Produktes ImageCard anzeigen.
 * 
 * Ist die Applikation im Admin-Modus, dann wird vor den Bildern ein UploadArea
 * angezeigt. Hier hat der Benutzer dann die Möglichkeit weitere Bilder
 * zum Produkt hochzuladen.
 * 
 * Alle Bilder der ImageGallery werden im Verzeichnis des Produktes gespeichert.
 * Zudem existiert eine Liste aller Bilder in der Datei 'images.json'.
 * Wird eine Bild neu hochgeladen oder ein Bild verändert, dann muss auch die 
 * Datei 'images.json' aktualisiert werden. 
 * 
 * Es besteht zudem die Möglichkeit die Reihenfolge der Bilder per Drag & Drop
 * zu verändern, dabei wird die geänderte Reihenfolge ebenso in der
 * Datei 'images.json' aktualisiert.
 * 
 * Alle Bilder der ImageGallery werden mit dem Prefix 'image_' und durch den aktuellen
 * Zeitstempel beim Hochladen ergänzt, sodass eindeutig erkennbar ist, dass das Bild 
 * zur ImageGallery gehört. 
 * Ursprünglicher Dateiname: 'ProduktBild01.jpg'
 * Dateiname im Produktverzeichnis: 'image_20251112084712_ProduktBild01.jpg' 
 *  
 */
import { useState, useEffect } from "react";
import { useSelector, useDispatch } from 'react-redux';
import update from "immutability-helper";
import * as utils from "../../../basics/utils";
import * as appActions from "../../../store/actions/appActions";
import * as fileActions from "../../../store/actions/fileActions";
import * as portfolioActions from "../../../store/actions/portfolioActions";
import { bindActionCreators } from 'redux';

import UploadArea from "../../partials/UploadArea";

import ImageCard from "./ImageCard";


const ImageGallery = () => {
    const app = useSelector((state) => state.app);
    const { path, appModus, orientation, inputHeight, galleryIndex, modalDialog, searchString, unitHeight, gallerySearch } = app;

    const portfolio = useSelector((state) => state.portfolio);
    const { type, currentProduct } = portfolio;

    const dispatch = useDispatch();
    const { setGalleryIndex, setGalleryImages, setSearchString } = bindActionCreators(appActions, dispatch);

    const { uploadJSON } = bindActionCreators(fileActions, dispatch);
    const { scanPortfolio } = bindActionCreators(portfolioActions, dispatch);

    const dialog = utils.getDialogByPath(path);

    const [foundList, setFoundList] = useState([]);

    // ==========================================================================
    // 
    // HELPER SECTION
    // 
    // ==========================================================================
    const searchImageList = (searchString, imageList) => {
        //console.log('searchImageList() imagesJSON:', imageList)

        let newFoundList = [];

        const filterImageFiles = (d) => {
            let imagesString = '';

            if (d['tags']) {
                imagesString += d['tags'];
            }

            const imagesX = imagesString.replace(/\s/g, '').toUpperCase();
            const searchX = searchString.replace(/\s/g, '').toUpperCase()

            if (imagesX.indexOf(searchX) !== -1) {
                newFoundList.push(d);
            }
        }

        if (searchString.length > 0) {
            for (const image of imageList) {
                filterImageFiles(image);
            }
        }

        if (newFoundList.length > 0) {
            setFoundList(newFoundList);
        } else {
            setFoundList(imageList);
        }
    }


    const getImagesJSON = () => {
        const productList = utils.getTree(path);

        const imagesJSON = productList.filter(d => d.name === 'images.json')
        if (imagesJSON.length > 0) {
            if (imagesJSON[0]['images']) {
                return imagesJSON[0]['images'];
            } else {
                return [];
            }
        }
        return [];
    }

    const doUploadJSON = (newImagesJSON) => {
        uploadJSON(path, 'images.json', newImagesJSON, () => {
            scanPortfolio(dialog.portfolio);
        }, (error) => {
            console.log('uploadJSON(images.json) error:', error);
        });
    }

    const onUploadImage = (file) => {
        let newImagesJSON = imagesJSON;
        newImagesJSON.push({ file: file, tags: [] });

        setImagesJSON(newImagesJSON);

        doUploadJSON(newImagesJSON);
    }

    const onDeleteImage = (file) => {
        let newImagesJSON = imagesJSON.filter(d => d.file !== file);
        setImagesJSON(newImagesJSON);
        setGalleryIndex(Math.max(newImagesJSON.length - 1, 0));

        doUploadJSON(newImagesJSON);
    };

    const onReorderImage = (index) => {
        setGalleryIndex(index);
        doUploadJSON(imagesJSON);
    };

    const moveImage = (from, to) => {
        setImagesJSON((prevImages) =>
            update(prevImages, {
                $splice: [
                    [from, 1],
                    [to, 0, prevImages[from]]
                ]
            })
        );
    };

    const [imagesJSON, setImagesJSON] = useState(getImagesJSON());

    // ==========================================================================
    //
    // LIFESYCLE - SECTION
    //
    // ==========================================================================

    /**
     * Falls sich das Produkt geändert hat, so müssen die vorhandenen Bilder
     * die dem Produkt zugeordnet wurden in den Zwiswchenspeicher 'imagesJSON' 
     * geladen werden.
     * Zusätzlich werden diese Bilder in den globalen redux-Store geladen werden.
     * damit sie auch im Dialog 'ImageTagModal' zur Verfügung stehen.  
     */
    useEffect(() => {
        //console.log(`useEffect(currentProduct)`, currentProduct?.name)

        const newImagesJSON = getImagesJSON();

        setImagesJSON(newImagesJSON);

        searchImageList(searchString, newImagesJSON);

        setGalleryImages(foundList.map((d, i) => ({ path: path, file: d.file, tags: d.tags })));

        setGalleryIndex(0);

    }, [currentProduct?.name]);

    /**
     * Falls sich das Produkt geändert hat, so müssen die vorhandenen Images aus 
     * Produktverzeichnis gelesen werden. 
     */
    useEffect(() => {
        //console.log('useEffect(galleryIndex): ', galleryIndex)

        searchImageList(searchString, imagesJSON);

        setGalleryImages(foundList.map((d, i) => ({ path: path, file: d.file, tags: d.tags })));

    }, [galleryIndex, modalDialog]);

    /**
     * Falls sich das Produkt geändert hat, so müssen die vorhandenen Images aus 
     * Produktverzeichnis gelesen werden. 
     */
    useEffect(() => {
        //console.log(`useEffect(searchString): ${searchString}`)

        searchImageList(searchString, imagesJSON);

        setGalleryImages(foundList.map((d, i) => ({ path: path, file: d.file, tags: d.tags })));

    }, [searchString]);

    // ==========================================================================
    // 
    // SUB RENDER
    // 
    // ==========================================================================

    const renderImageCardNew = () => {
        if (appModus !== 'Admin') return null;

        const itemHeight = (orientation === 'landscape') ? inputHeight / 4 : inputHeight / 6

        const style = {
            width: itemHeight,
            minWidth: itemHeight,
        }

        return (
            <div className="flex items-start justify-center border-gray-600 p-2 pb-4" style={style}>
                <UploadArea uploadArea='images' uploadType='product' uploadPath={path} uploadPost='' onUploadCallback={onUploadImage} />
            </div>
        )
    };

    /**
     * Zwischen der Filter-Liste und der Liste der Suchergebnisse wird das Textfeld
     * zur Eingabe des Suchbegriffs angezeigt. Die Debounce-Mechanik verhindert, dass
     * direkt nach Eingabe eines oder mehrerer Zeichen in kurzer Folge, sofort gesucht wird.
     */
    const renderSearchString = () => {

        // Wenn Eingabefeld verändert wurde
        const onChange = (e) => {
            setSearchString(e.target.value)
        }

        // ###############################################################
        // renderSearchString()
        // ###############################################################
        const style = utils.getStyleHeight(unitHeight, .4, .4, .3);

        return (
            <div className="w-full flex flex-row border-b-2 border-gray-300 text-gray-800" style={style}>
                <input className="w-full focus:outline-none bg-transparent"
                    placeholder="" onChange={onChange} value={searchString} autoFocus />
            </div>
        )
    }

    /**
     * Falls Die Suche aktiviert wurde, so wird vor den Bildern ein Bereich (Card) angezeigt
     * in dem der Benutzer einen SearchString eingeben kann. 
     * ANMERKUNG: Diese Card wird jedoch nicht im Admin-Mode angezeigt, da die Anzeige der 
     * Schlagwörter nicht korrekt funktioniert.
     */
    const renderImageCardSearch = () => {
        if (appModus === 'Admin') return null;
        if (!gallerySearch) return null;

        const itemHeight = (orientation === 'landscape') ? inputHeight / 4 : inputHeight / 6

        const style = {
            width: itemHeight,
            minWidth: itemHeight,
        }

        return (
            <div className="flex items-end justify-center border-gray-600 p-2 pb-8" style={style}>
                {renderSearchString()}
            </div>
        )
    };

    /**
     * Alle Bilder anzeigen
     */
    const renderImages = (images) => {
        return (
            <>
                {images.map((img, idx) => (
                    <ImageCard key={idx} image={img} index={idx} moveImage={moveImage} onReorderImage={onReorderImage} onDeleteImage={onDeleteImage} />
                ))}
            </>
        )
    }

    // ==========================================================================
    // 
    // MAIN RENDER
    // 
    // ==========================================================================
    const className = (orientation === 'portrait') ? 'flex-col overflow-y-auto overflow-x-hidden' : 'flex-row overflow-x-auto overflow-y-hidden';

    return (
        <div key={type} className={`flex w-full h-full ${className}`}>
            {renderImageCardNew()}
            {renderImageCardSearch()}

            {foundList.length > 0 && searchString.length !== 0 ? renderImages(foundList) : renderImages(imagesJSON)}

        </div>
    );
};

export default ImageGallery;


